<template>
  <v-form ref="FRACTION_DSN" @submit.prevent="sectionSave" lazy-validation class="section">
    <div>
      <section-header v-if="!hideTitle"
                      :ressource="ressource"
                      :isExtended="extended"
                      :dialog="dialog"
                      :picto="picto()"
                      :fiche=fiche
                      :isAbstract=isAbstract
                      @extended=setExtended
                      @closeDialog=closeDialog
                      ref="FRACTION_DSN">
      </section-header>
      <div v-if="extended" id="field-container" :class="dialog ? 'p-0 p-md-3' : 'py-2'">
        <fraction-dsn-genre v-if="isDisplayed('GENRE')"
                            ref="GENRE"
                            :theme="theme"
                            :showMissing="showMissing"
                            v-model:value=genre
                            @status=statusGenre
                            :required="isRequired('GENRE')"
                            :contextId=fractionDsn.id
                            :readOnly="readOnly('GENRE')"
                            :objType=objType
                            :fiche=fiche
                            :sectionName="sectionName">
        </fraction-dsn-genre>
        <fraction-dsn-type v-if="isDisplayed('TYPE')"
                           ref="TYPE"
                           :theme="theme"
                           :showMissing="showMissing"
                           v-model:value=type
                           @status=statusType
                           :required="isRequired('TYPE')"
                           :contextId=fractionDsn.id
                           :readOnly="readOnly('TYPE')"
                           :objType=objType
                           :fiche=fiche
                           :sectionName="sectionName">
        </fraction-dsn-type>
        <fraction-dsn-fraction v-if="isDisplayed('FRACTION')"
                               ref="FRACTION"
                               :theme="theme"
                               :showMissing="showMissing"
                               v-model:value=fraction
                               :min="1"
                               :max="6"
                               @status=statusFraction
                               :required="isRequired('FRACTION')"
                               :contextId=fractionDsn.id
                               :readOnly="readOnly('FRACTION')"
                               :objType=objType
                               :fiche=fiche
                               :sectionName="sectionName">
        </fraction-dsn-fraction>
        <fraction-dsn-entite v-if="isDisplayed('ENTITE')"
                             ref="ENTITE"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=entite
                             :min="1"
                             :max="50"
                             @status=statusEntite
                             :over-ride-placeholder=placeholderEntite
                             :required="isRequired('ENTITE')"
                             :contextId=fractionDsn.id
                             :readOnly="readOnly('ENTITE')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </fraction-dsn-entite>
        <fraction-dsn-nom v-if="isDisplayed('NOM')"
                          ref="NOM"
                          :theme="theme"
                          :showMissing="showMissing"
                          v-model:value=nom
                          :min="3"
                          :max="25"
                          @status=statusNom
                          :over-ride-placeholder=placeholderNom
                          :required="isRequired('NOM')"
                          :contextId=fractionDsn.id
                          :readOnly="readOnly('NOM')"
                          :objType=objType
                          :fiche=fiche
                          :sectionName="sectionName">
        </fraction-dsn-nom>
        <fraction-dsn-prenom v-if="isDisplayed('PRENOM')"
                             ref="PRENOM"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=prenom
                             :min="3"
                             :max="25"
                             @status=statusPrenom
                             :over-ride-placeholder=placeholderPrenom
                             :required="isRequired('PRENOM')"
                             :contextId=fractionDsn.id
                             :readOnly="readOnly('PRENOM')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </fraction-dsn-prenom>
        <fraction-dsn-mail v-if="isDisplayed('MAIL')"
                           ref="MAIL"
                           :theme="theme"
                           :showMissing="showMissing"
                           v-model:value=mail
                           @status=statusMail
                           :over-ride-placeholder=placeholderMail
                           :required="isRequired('MAIL')"
                           :contextId=fractionDsn.id
                           :readOnly="readOnly('MAIL')"
                           :objType=objType
                           :fiche=fiche
                           :sectionName="sectionName">
        </fraction-dsn-mail>
        <fraction-dsn-numero v-if="isDisplayed('NUMERO')"
                             ref="NUMERO"
                             :theme="theme"
                             :showMissing="showMissing"
                             v-model:value=numero
                             @status=statusNumero
                             :over-ride-placeholder=placeholderNumero
                             :required="isRequired('NUMERO')"
                             :contextId=fractionDsn.id
                             :readOnly="readOnly('NUMERO')"
                             :objType=objType
                             :fiche=fiche
                             :sectionName="sectionName">
        </fraction-dsn-numero>
      </div>
    </div>
    <div v-if="extended" class="d-flex flex-row-reverse">
      <loader-button v-if="saveButton"
                     class="ms-2"
                     type="submit"
                     :loading="saveLoading"
                     :label="saveLabel"
                     :disabled="!updatedAndValid">
      </loader-button>
      <div v-for="(button, index) in extraButtons" :key="index">
        <loader-button class="ms-2"
                       :outlined="button.outlined"
                       @callback="button.callback()"
                       :type="button.type"
                       :label="button.label"
                       :loading="button.loading"
                       :disabled="button.disabled">
        </loader-button>
      </div>
    </div>
    <div class="d-flex flex-row-reverse" v-if="saveFailed && saveButton && extended">
      <p class="text-12 text-bold error-text">{{errorTxt}}</p>
    </div>
  </v-form>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import CrudFractionDsn from '@/generated/cruds/CrudFractionDsn';
  // eslint-disable-next-line
  import { GenControl, GenSection, RessourceGenControl, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { FwkButtons, RessourceFwkButton, RessourceFwkSection, FwkSections } from '@/ressources/globalRessources';
  import { Sections } from '@/generated/classes/AppSchema';
  import Button, { ButtonCallback } from '@/framework/base/classes/Button';
  import { ObjTypes } from '@/generated/classes/ObjTypes';

  export default defineComponent({
    extends:  CrudFractionDsn,
    components: {},
    props: {
      hideTitle: {
        type: Boolean,
      },
      isAbstract: {
        type: Boolean,
      },
    },

    data() {
      return {
        sectionName: Sections.FRACTION_DSN,
        displayMap: new Map<string, Boolean>,
        readOnlyMap: new Map<string, Boolean>,
        requiredMap: new Map<string, Boolean>,
        placeholderEntite: null,
        placeholderNom: null,
        placeholderPrenom: null,
        placeholderMail: null,
        placeholderNumero: null,
      }
    },

    computed: {
      saveLabel () { 
        return RessourceFwkButton(FwkButtons.ENREGISTRER).label;
      },

      errorTxt () { 
        return RessourceFwkSection(FwkSections.ERROR_TEXT).label;
      },


      ressource () { return RessourceGenSection(GenSection.FRACTION_DSN); },

      fields (): string[] {
        return ['GENRE', 'TYPE', 'FRACTION', 'ENTITE', 'NOM', 'PRENOM', 'MAIL', 'NUMERO'];
      },

      extraButtons (): Button[] {
        return [];
      },

    },
    methods: {
      getRequiredAttributs(): string[] {
        const res: string[] = [];
        if (this.requiredAttributs) {
          this.requiredAttributs.forEach((attrib) => {
            const value = attrib.split(".");
            if (value[0] == this.sectionName) {
              res.push(value[1])
            }
          })
        }
        return res;
      },

      requiredMissing(requiredAttributs: string[], value: string): boolean {
        return requiredAttributs.length > 0 && requiredAttributs.includes(value);;
      },

      readOnly (value: string): boolean {
        let res = false;
        if (this.readOnlyMap.size == 0) {
          this.readOnlyMap.set("GENRE", false);
          this.readOnlyMap.set("TYPE", false);
          this.readOnlyMap.set("FRACTION", false);
          this.readOnlyMap.set("ENTITE", false);
          this.readOnlyMap.set("NOM", false);
          this.readOnlyMap.set("PRENOM", false);
          this.readOnlyMap.set("MAIL", false);
          this.readOnlyMap.set("NUMERO", false);
        }
        const readOnly = this.readOnlyMap.get(value);
        if (readOnly != undefined) {
          res = readOnly;
        }
        return res;
      },

      isDisplayed (value: string): boolean {
        let res = true;
        if (this.displayMap.size == 0) {
        const requiredAttributs = this.getRequiredAttributs();
          this.displayMap.set("GENRE", true);
          this.displayMap.set("TYPE", true);
          this.displayMap.set("FRACTION", this.requiredMissing(requiredAttributs, "FRACTION") || !!this.fiche);
          this.displayMap.set("ENTITE", this.requiredMissing(requiredAttributs, "ENTITE") || !!this.fiche);
          this.displayMap.set("NOM", this.requiredMissing(requiredAttributs, "NOM") || !!this.fiche);
          this.displayMap.set("PRENOM", this.requiredMissing(requiredAttributs, "PRENOM") || !!this.fiche);
          this.displayMap.set("MAIL", this.requiredMissing(requiredAttributs, "MAIL") || !!this.fiche);
          this.displayMap.set("NUMERO", this.requiredMissing(requiredAttributs, "NUMERO") || !!this.fiche);
        }
        const display = this.displayMap.get(value);
        if (display != undefined) {
          res = display;
        }
        return res;
      },

      isRequired (value: string): boolean {
        let res = true;
        const requiredAttributs = this.getRequiredAttributs();
        if (this.requiredMap.size == 0) {
          this.requiredMap.set("GENRE", true);
          this.requiredMap.set("TYPE", true);
          this.requiredMap.set("FRACTION", this.requiredMissing(requiredAttributs, "FRACTION") || !this.fiche);
          this.requiredMap.set("ENTITE", this.requiredMissing(requiredAttributs, "ENTITE") || !this.fiche);
          this.requiredMap.set("NOM", this.requiredMissing(requiredAttributs, "NOM") || !this.fiche);
          this.requiredMap.set("PRENOM", this.requiredMissing(requiredAttributs, "PRENOM") || !this.fiche);
          this.requiredMap.set("MAIL", this.requiredMissing(requiredAttributs, "MAIL") || !this.fiche);
          this.requiredMap.set("NUMERO", this.requiredMissing(requiredAttributs, "NUMERO") || !this.fiche);
        }
        const required = this.requiredMap.get(value);
        if (required != undefined) {
          res = required;
        }
        return res;
      },

      picto (): string {
        return 'SectionPictoDsn' 
      },

      sectionSave (): void {
        return this.save();
      }
    }
  })
</script>
